// MOUNT IN main.tsx:
//   import { ToastProvider } from './components/ToastProvider'
//   ...wrap RouterProvider in <ToastProvider>...</ToastProvider>

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { Toast, type ToastAction, type ToastItem, type ToastVariant } from './Toast'

const DEFAULT_DURATION_MS = 5000

/**
 * Per-toast overrides. Both optional and additive — existing callers pass
 * a bare message and keep the default duration with no action.
 */
export interface ToastOptions {
  /** Auto-dismiss after this many ms instead of the provider default. */
  durationMs?: number
  /** One follow-up action rendered beside the message (e.g. "Manage storage"). */
  action?: ToastAction
}

interface ToastApi {
  /**
   * Each shows a toast and returns its id, so a caller that raises a
   * progress toast can retire it the moment the outcome lands instead
   * of leaving both on screen until the auto-dismiss timer expires.
   * The return is additive — callers that ignore it are unaffected.
   */
  success: (message: string, options?: ToastOptions) => string
  error: (message: string, options?: ToastOptions) => string
  info: (message: string, options?: ToastOptions) => string
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

interface ToastProviderProps {
  children: ReactNode
  /** Override auto-dismiss duration. Default: 5000ms. */
  durationMs?: number
}

export function ToastProvider({ children, durationMs = DEFAULT_DURATION_MS }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timersRef = useRef<Map<string, number>>(new Map())

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id))
    const timer = timersRef.current.get(id)
    if (timer !== undefined) {
      window.clearTimeout(timer)
      timersRef.current.delete(id)
    }
  }, [])

  const push = useCallback(
    (variant: ToastVariant, message: string, options?: ToastOptions) => {
      const id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const item: ToastItem = { id, message, variant }
      if (options?.action) item.action = options.action
      setToasts((current) => [...current, item])
      const timerId = window.setTimeout(() => dismiss(id), options?.durationMs ?? durationMs)
      timersRef.current.set(id, timerId)
      return id
    },
    [dismiss, durationMs],
  )

  const api = useMemo<ToastApi>(
    () => ({
      success: (message: string, options?: ToastOptions) => push('success', message, options),
      error: (message: string, options?: ToastOptions) => push('error', message, options),
      info: (message: string, options?: ToastOptions) => push('info', message, options),
      dismiss,
    }),
    [dismiss, push],
  )

  // Clear any pending timers on unmount.
  useEffect(() => {
    const timers = timersRef.current
    return () => {
      for (const timerId of timers.values()) {
        window.clearTimeout(timerId)
      }
      timers.clear()
    }
  }, [])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed bottom-4 end-4 z-[60] flex flex-col gap-2">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within <ToastProvider>')
  }
  return ctx
}
